// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/components/GuildOverviewTabSettingsSection';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import type {ChannelLike} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTypes';
import type {FormInputs, SelectOption} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {afkTimeoutOptionsRaw} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {CUSTOM_ELLIPSIS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Select as FormSelect} from '@app/features/ui/components/form/FormSelect';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Controller} from 'react-hook-form';

const NO_AFK_CHANNEL_DESCRIPTOR = msg({
	message: 'No AFK channel',
	comment: 'Empty-state text in the idle settings section.',
});
const UNNAMED_CHANNEL_DESCRIPTOR = msg({
	message: 'Unnamed channel',
	comment: 'Short label in the idle settings section. Keep it concise.',
});
const AFK_IDLE_CHANNEL_DESCRIPTOR = msg({
	message: 'AFK / idle channel',
	comment: 'Label in the idle settings section.',
});
const MOVE_MEMBERS_TO_THIS_CHANNEL_WHEN_THEY_RE_DESCRIPTOR = msg({
	message: "Move members to this channel when they're AFK.",
	comment: 'Description text in the idle settings section.',
});
const SELECT_A_CHANNEL_DESCRIPTOR = msg({
	message: 'Select a channel',
	comment: 'Button or menu action label in the idle settings section. Keep it concise.',
});
const AFK_TIMEOUT_DESCRIPTOR = msg({
	message: 'AFK timeout',
	comment: 'Short label in the idle settings section. Keep it concise.',
});
const CUSTOM_AFK_TIMEOUT_SECONDS_DESCRIPTOR = msg({
	message: 'Custom AFK timeout (seconds)',
	comment: 'Label in the idle settings section.',
});
const ANY_VALUE_FROM_TO_SECONDS_DESCRIPTOR = msg({
	message: 'Any value from {afkTimeoutMinSeconds} to {afkTimeoutMaxSeconds} seconds.',
	comment:
		'Description text in the idle settings section. Preserve {afkTimeoutMinSeconds}, {afkTimeoutMaxSeconds}; they are inserted by code.',
});
const AFK_TIMEOUT_MIN_SECONDS = 60;
const AFK_TIMEOUT_MAX_SECONDS = 3600;
const AFK_TIMEOUT_CUSTOM_SENTINEL = -1;
export const IdleSettingsSection: React.FC<{
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	voiceChannels: Array<ChannelLike>;
}> = ({form, canManageGuild, voiceChannels}) => {
	const {i18n} = useLingui();
	const afkTimeoutOptions = useMemo<Array<{value: number; label: string}>>(
		() => [
			...afkTimeoutOptionsRaw.map(({value, label}) => ({value, label: i18n._(label)})),
			{value: AFK_TIMEOUT_CUSTOM_SENTINEL, label: i18n._(CUSTOM_ELLIPSIS_DESCRIPTOR)},
		],
		[i18n],
	);
	const afkPresetValues = useMemo(() => new Set(afkTimeoutOptionsRaw.map((o) => o.value)), []);
	const currentAfkTimeout = form.watch('afk_timeout');
	const [isAfkTimeoutCustom, setIsAfkTimeoutCustom] = useState(
		typeof currentAfkTimeout === 'number' && !afkPresetValues.has(currentAfkTimeout),
	);
	useEffect(() => {
		if (typeof currentAfkTimeout !== 'number') return;
		if (!afkPresetValues.has(currentAfkTimeout)) {
			setIsAfkTimeoutCustom(true);
		}
	}, [afkPresetValues, currentAfkTimeout]);
	const afkChannelOptions = useMemo<Array<SelectOption>>(
		() => [
			{value: null, label: i18n._(NO_AFK_CHANNEL_DESCRIPTOR)},
			...voiceChannels.map((channel) => ({
				value: channel.id,
				label: channel.name ?? i18n._(UNNAMED_CHANNEL_DESCRIPTOR),
			})),
		],
		[voiceChannels],
	);
	return (
		<SettingsSection
			title={<Trans>Idle settings</Trans>}
			description={<Trans>Configure AFK channel and timeout</Trans>}
			data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.settings-section"
		>
			<div
				className={styles.settingsContent}
				data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.settings-content"
			>
				<div data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.div">
					<Controller
						name="afk_channel_id"
						control={form.control}
						render={({field}) => (
							<FormSelect<string | null>
								label={i18n._(AFK_IDLE_CHANNEL_DESCRIPTOR)}
								description={i18n._(MOVE_MEMBERS_TO_THIS_CHANNEL_WHEN_THEY_RE_DESCRIPTOR)}
								value={field.value ?? null}
								onChange={(v) => field.onChange(v)}
								options={afkChannelOptions}
								placeholder={i18n._(SELECT_A_CHANNEL_DESCRIPTOR)}
								disabled={!canManageGuild}
								data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.form-select.change"
							/>
						)}
						data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.controller"
					/>
				</div>
				<div data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.div--2">
					<Controller
						name="afk_timeout"
						control={form.control}
						render={({field}) => {
							const currentValue = field.value ?? 300;
							const selectValue = isAfkTimeoutCustom ? AFK_TIMEOUT_CUSTOM_SENTINEL : currentValue;
							return (
								<>
									<FormSelect<number>
										label={i18n._(AFK_TIMEOUT_DESCRIPTOR)}
										value={selectValue}
										onChange={(v) => {
											if (v === AFK_TIMEOUT_CUSTOM_SENTINEL) {
												setIsAfkTimeoutCustom(true);
												return;
											}
											setIsAfkTimeoutCustom(false);
											field.onChange(v);
										}}
										options={afkTimeoutOptions}
										disabled={!canManageGuild}
										data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.form-select.set-is-afk-timeout-custom"
									/>
									{isAfkTimeoutCustom && (
										<Input
											type="number"
											label={i18n._(CUSTOM_AFK_TIMEOUT_SECONDS_DESCRIPTOR)}
											footer={i18n._(ANY_VALUE_FROM_TO_SECONDS_DESCRIPTOR, {
												afkTimeoutMinSeconds: AFK_TIMEOUT_MIN_SECONDS,
												afkTimeoutMaxSeconds: AFK_TIMEOUT_MAX_SECONDS,
											})}
											min={AFK_TIMEOUT_MIN_SECONDS}
											max={AFK_TIMEOUT_MAX_SECONDS}
											step={1}
											value={String(currentValue)}
											onChange={(event) => {
												const raw = event.target.value;
												if (raw === '') return;
												const parsed = Number.parseInt(raw, 10);
												if (Number.isNaN(parsed)) return;
												const clamped = Math.max(AFK_TIMEOUT_MIN_SECONDS, Math.min(AFK_TIMEOUT_MAX_SECONDS, parsed));
												field.onChange(clamped);
											}}
											disabled={!canManageGuild}
											data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.input.change.number"
										/>
									)}
								</>
							);
						}}
						data-flx="guild.guild-tabs.guild-overview-tab.idle-settings-section.controller--2"
					/>
				</div>
			</div>
		</SettingsSection>
	);
};
